import { useEffect, useRef, useState } from "react";
import i18n from "i18next";

import { Menu, MenuItem, Sidebar as ProSidebar, SubMenu, menuClasses } from 'react-pro-sidebar';
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import reduxUtil from "@plannel/utils/redux-util";

import HelpIcon from '@mui/icons-material/Help';

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import logo from "@plannel/assets/icons/logo.png";
import compactLogo from "@plannel/assets/icons/compact-logo.png";

import tabMenuList from "@plannel/pages/TabMenuList"
import { isEqual, isNil } from "lodash";
import userService from "@plannel/services/system/user-service";
import debug from "@plannel/utils/debug-util";
import { Divider } from "@mui/material";
import CodeUtil from "@plannel/pages/utils/code-util";
import menuAccessUtil from "@plannel/pages/utils/menu-access-util";
import { FS } from "@plannel/utils/font-util";

const MODULE_GROUP_COLOR = "#99C5CF";
const CLICKED_MENU = "CLICKED_MENU";
const DASHBOARD = "dashboard";

const MENU_CLASSES_DISABLED = `&.${menuClasses.disabled}`;
const HOVER = '&:hover';
const SIDEBAR_WIDTH = '270px';

// 메뉴 툴팁은 힌트일 뿐 — 포인터를 받으면 바로 옆 팝아웃 메뉴를 덮어 클릭을 막는다.
const TOOLTIP_SLOT_PROPS = { popper: { sx: { pointerEvents: "none" } } };
const isInPopout = (e) => !!e?.target?.closest?.(`.${menuClasses.subMenuContent}`);
const Sidebar = ({ t, handleAddTab }) => {
  const settings = JSON.parse(localStorage.getItem("settings"));
  const userData = JSON.parse(localStorage.getItem("user"));
  const [userAccess, setUserAccess] = useState(null);

  const themes = {
    sidebar: {
      /* Sidebar root back-ground Color  */
      backgroundColor: '#048BA8',
    },
    root: {
      fontSize: FS.H1,
    },
    collapsed: {
      /* 접힌 상태에서의 button 안쪽 간격 */
      button: {
        0: { padding: '5px 30px 0px 18px' },
        1: { padding: '6px 20px 5px 15px' },
        2: { padding: '1px 10px 0px 20px' },
      },
      /* 접힌 상태에서의 menuContent 안쪽 간격 */
      menuContent: {
        /* Sidebar안의 Menu content들에 대한 스타일 정보 3단계구성 (Main: 0, Sub: 1, Leaf: 2) */
        0: { backgroundColor: '#025B6E', width: SIDEBAR_WIDTH, maxHeight: '90vh', overflowY: 'auto', scrollbarWidth: 'thin' },
        1: { backgroundColor: '#025B6E' },
        2: { backgroundColor: '#025B6E' },
      },
    },
    menuContent: {
      /* Sidebar안의 Menu content들에 대한 스타일 정보 3단계구성 (Main: 0, Sub: 1, Leaf: 2) */
      0: { backgroundColor: '#025B6E', width: SIDEBAR_WIDTH, overflowY: 'auto', scrollbarWidth: 'none' },
      1: { backgroundColor: '#025B6E' },
      2: { backgroundColor: '#025B6E' },
    },
    icon: {
      /* 기본 컬러사용, Color 지정시 최우선 순위로 지정되며 css 파일 우선순위보다 높음 (!important 무시) */
      color: undefined,
    },
    button: {
      /* (Main: 0, Sub: 1, Leaf: 2) 해당 button 마다 style 지정. height 지정하지 않을시 기본값 50px */
      0: { padding: '5px 15px 0px 18px', [MENU_CLASSES_DISABLED]: { color: '#3e5e7e' }, [HOVER]: { backgroundColor: 'transparent', color: '#F18F01' } },
      1: { padding: '5px 20px 5px 15px', [MENU_CLASSES_DISABLED]: { color: '#3e5e7e' }, [HOVER]: { backgroundColor: 'transparent', color: '#F18F01' }, height: 'auto' },
      2: { padding: '0px 10px 0px 20px', [MENU_CLASSES_DISABLED]: { color: '#3e5e7e' }, [HOVER]: { backgroundColor: 'transparent', color: '#F18F01' }, height: 'auto' },
    },
    label: {
      /* Button 안의 Label text 값 (SubMenu의 경우 Label 값을 지정해 주어야 함.) */
      whiteSpace: 'normal',
    },
    subMenuExpandIcon: {
      /*
      * Expand Icon을 지정. 초기 Icon은 변경하기 힘들기 때문에 기존 (>) 화살표의 디자인을 그대로 가져옴 Collapsed 된 경우 dot이 기본.
      * contentVisibility: "hidden"을 사용하여 안쪽 child(content)에 대한 visibility를 숨김.
      * open 상태일 경우 rotate(-45deg) -> rotate(45deg)로 변경하여 화살표를 만듬.
      */
      color: '#FFFFFF',
      display: 'inline-block',
      transition: 'transform 0.3s',
      borderRight: '2px solid currentcolor',
      borderBottom: '2px solid currentcolor',
      width: '5px',
      height: '5px',
      transform: 'rotate(-45deg)',
      contentVisibility: 'hidden',
    },
    open: {
      subMenuExpandIcon: {
        transform: 'rotate(45deg)',
      },
    }
  };

  const getSidebarCollapsedFromLS = () => {
    return settings.sbcollapsed;
  };
  const [sideMenuList, setSideMenuList] = useState(() => tabMenuList.makeSideMenuList());

  useEffect(() => {
    if (!isNil(userData) && userData?.roles?.includes(CodeUtil.roles.APP_BI)) {
      tabMenuList.getArReportMenu().then(() => {
        setSideMenuList(tabMenuList.makeSideMenuList());
      });
    }

    const handleLanguageChanged = () => {
      tabMenuList.rebuildArReportMenu();
      setSideMenuList(tabMenuList.makeSideMenuList());
    };
    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reduxClickedMenuState = reduxUtil.getViewState(CLICKED_MENU);
  const reduxDispatch = useDispatch();

  const [sbCollapsed, setSbCollapsed] = useState(getSidebarCollapsedFromLS());
  const [activeMainTitle, setActiveMainTitle] = useState(reduxClickedMenuState?.mainTitle ? reduxClickedMenuState.mainTitle : null);
  const [openedMainTooltip, setOpenedMainTooltip] = useState(null);
  // 툴팁은 hover 후 enterDelay(100ms) 타이머로 열린다. 그 사이 클릭하면 클릭 때 닫은 툴팁을 타이머가 다시 연다.
  // 타이머 콜백은 hover 시점의 클로저라 state 가 낡았으므로 ref 로 최신 활성 메뉴를 본다.
  const activeMainTitleRef = useRef(activeMainTitle);
  activeMainTitleRef.current = activeMainTitle;
  /* LV2 Menu의 경우 중복될 수 있기에 Unique한 Menu code로 Active를 체크합니다. */
  const [activeSubMenu, setActiveSubMenu] = useState(reduxClickedMenuState?.subMenu ? reduxClickedMenuState?.subMenu : null);
  const [activeLeafTitle, setActiveLeafTitle] = useState(reduxClickedMenuState?.leafTitle ? reduxClickedMenuState?.leafTitle : null);

  const menuItemStyles = {
    root: themes.root,
    icon: themes.icon,
    SubMenuExpandIcon: (props) => {
      const { open, level } = props;

      if ((open && level !== 0) || (!sbCollapsed && open)) {
        return { ...themes.subMenuExpandIcon, ...themes.open.subMenuExpandIcon };
      }

      return themes.subMenuExpandIcon;
    },
    subMenuContent: (props) => {
      const { level } = props;

      if (sbCollapsed) {
        return themes.collapsed.menuContent[level];
      }

      return themes.menuContent[level];
    },
    button: (props) => {
      const { level } = props;

      if (sbCollapsed) {
        return { ...themes.button[level], ...themes.collapsed.button[level] };
      }

      return themes.button[level];
    },
    label: (props) => {
      const { open } = props;

      return {
        ...themes.label,
        fontWeight: open ? 600 : undefined
      }
    },
  };

  const handleCollapsedChange = () => {
    const changedState = !sbCollapsed;
    setSbCollapsed(changedState);

    settings.sbcollapsed = changedState;
    localStorage.setItem("settings", JSON.stringify(settings));
  };

  const setMainActive = (mainTitle, key) => {
    if (key) {
      handleAddTab(key);
    }
    setOpenedMainTooltip(null);
    activeMainTitleRef.current = mainTitle;
    setActiveMainTitle(mainTitle);

    const reduxMenuState = reduxUtil.getViewState(CLICKED_MENU);
    if (reduxMenuState && isEqual(mainTitle, DASHBOARD)) {
      reduxMenuState.subMenu = null;
    }

    const reduxState = reduxUtil.createObjectState(CLICKED_MENU, { ...reduxMenuState, mainTitle });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
  }

  const setSubActive = (menuCd) => {
    setActiveSubMenu(menuCd);

    const reduxMenuState = reduxUtil.getViewState(CLICKED_MENU);
    const reduxState = reduxUtil.createObjectState(CLICKED_MENU, { ...reduxMenuState, subMenu: menuCd });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
  }

  const setLeafActive = (leafTitle, key) => {
    handleAddTab(key);
    setActiveLeafTitle(leafTitle);

    const reduxMenuState = reduxUtil.getViewState(CLICKED_MENU);
    const reduxState = reduxUtil.createObjectState(CLICKED_MENU, { ...reduxMenuState, leafTitle });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
  }

  const getClassName = (uniqueTitle, type) => {
    if (type === "leaf") {
      if (uniqueTitle === activeLeafTitle) {
        return "sidebar-active sidebar-leaf";
      }
      return "sidebar-inactive sidebar-leaf";
    }
    else if ((type === "root" && uniqueTitle === activeMainTitle) || (type === "sub" && uniqueTitle === activeSubMenu)) {
      return "sidebar-active";
    }

    return "sidebar-inactive";
  }

  const roleCheck = (appRoles, userRoles, reduxKey) => {
    return menuAccessUtil.roleCheck(userAccess, appRoles, userRoles, reduxKey);
  }

  const SidebarLeafMenu = (leaf) => {
    const { title, icon, key, appRoles, userRoles, hide, reduxKey, skipI18n } = leaf;
    if (roleCheck(appRoles, userRoles, reduxKey) && !hide) {
      const displayTitle = skipI18n ? title : t(title);
      return (
        <Tooltip key={title} title={displayTitle} placement="right" disableInteractive slotProps={TOOLTIP_SLOT_PROPS}>
          <span style={{ display: "block" }}>
            <MenuItem data-testid={title} style={{ fontSize: FS.TITLE }} icon={icon} className={getClassName(title, "leaf")} onClick={() => setLeafActive(title, key)}>{displayTitle}</MenuItem>
          </span>
        </Tooltip>
      )
    }
    return "";
  }

  // groupType으로 메뉴를 그룹화하고 정렬하는 함수
  const groupAndSortLeafMenus = (lv3Menu) => {
    if (!lv3Menu || lv3Menu.length === 0) return [];

    // groupType이 있는 메뉴들을 그룹화
    const grouped = {};
    const ungrouped = [];

    lv3Menu.forEach((menu) => {
      if (menu.groupType) {
        if (!grouped[menu.groupType]) {
          grouped[menu.groupType] = [];
        }
        grouped[menu.groupType].push(menu);
      } else {
        ungrouped.push(menu);
      }
    });

    // 각 그룹 내에서 key로 정렬
    Object.keys(grouped).forEach((groupKey) => {
      grouped[groupKey].sort((a, b) => a.key - b.key);
    });

    // MENU_GROUP 순서에 따라 그룹 정렬
    const groupOrder = Object.values(tabMenuList.MENU_GROUP);
    const sortedGroupKeys = Object.keys(grouped).sort((a, b) => {
      return groupOrder.indexOf(a) - groupOrder.indexOf(b);
    });

    return { grouped, ungrouped, sortedGroupKeys };
  };

  // 그룹화된 메뉴 렌더링
  const renderGroupedLeafMenus = (lv3Menu) => {
    const { grouped, ungrouped, sortedGroupKeys } = groupAndSortLeafMenus(lv3Menu);
    
    if (!sortedGroupKeys || sortedGroupKeys.length === 0) {
      // 그룹이 없으면 기존 방식으로 렌더링
      return lv3Menu?.map((item) => SidebarLeafMenu(item));
    }

    const result = [];

    // 그룹화된 메뉴 렌더링
    sortedGroupKeys.forEach((groupKey) => {
      const groupMenus = grouped[groupKey];
      // 권한 체크를 통과하는 메뉴가 있는지 확인
      const hasVisibleMenus = groupMenus.some((menu) => 
        roleCheck(menu.appRoles, menu.userRoles, menu.reduxKey) && !menu.hide
      );

      if (hasVisibleMenus) {
        // 그룹 헤더 추가
        result.push(
          <Box 
            key={`group-header-${groupKey}`} 
            sx={{ 
              padding: '8px 20px 4px 20px',
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                color: MODULE_GROUP_COLOR,
                fontWeight: 600,
                fontSize: FS.TITLE,
                // textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {t(groupKey)}
            </Typography>
            <Divider sx={{ backgroundColor: MODULE_GROUP_COLOR, opacity: 0.5 }} />
          </Box>
        );

        // 그룹 내 메뉴들 렌더링
        groupMenus.forEach((menu) => {
          const rendered = SidebarLeafMenu(menu);
          if (rendered) {
            result.push(rendered);
          }
        });
      }
    });

    // 그룹화되지 않은 메뉴들 렌더링
    if (ungrouped.length > 0) {
      ungrouped.forEach((menu) => {
        const rendered = SidebarLeafMenu(menu);
        if (rendered) {
          result.push(rendered);
        }
      });
    }

    return result;
  };

  const SidebarSubMenu = (sub) => {
    const { menuTitle, menuCd, icon, lv3Menu, appRoles, userRoles, reduxKey } = sub;
    if (roleCheck(appRoles, userRoles, reduxKey)) {
      return (
        <SubMenu key={menuTitle} data-testid={menuCd} label={t(menuTitle)} icon={icon} className={getClassName(menuCd, "sub")} onClick={() => setSubActive(menuCd)}>
          {lv3Menu?.length > 0 ? renderGroupedLeafMenus(lv3Menu) : ""}
        </SubMenu>
      )
    }
    return ""
  }

  const SidebarMainMenu = (root) => {
    const { sideMenu, icon, key, title, lv2Menu, subMenu, singleMenu, appRoles, userRoles, reduxKey } = root;
    if (sideMenu && !singleMenu && roleCheck(appRoles, userRoles, reduxKey)) {
      return (
        // 접힌 상태에서만 대분류 라벨을 크림 툴팁으로 힌트. 펼치면 라벨이 보이고 자식 hover 오작동 방지 위해 title 비움.
        // 팝아웃(ps-submenu-content)은 DOM 상 이 span 안에 있어서, 팝아웃으로 포인터를 옮겨도 mouseleave 가 안 나고
        // 툴팁이 팝아웃 항목 위에 남는다 → 팝아웃 안에서는 열지 않고, 이미 열려 있으면 닫는다.
        <Tooltip
          key={key}
          title={sbCollapsed ? t(title) : ""}
          placement="right"
          open={sbCollapsed && openedMainTooltip === title}
          onOpen={(e) => { if (!isInPopout(e) && activeMainTitleRef.current !== title) setOpenedMainTooltip(title); }}
          onClose={() => setOpenedMainTooltip(null)}
          disableHoverListener={activeMainTitle === title}
          disableInteractive
          slotProps={TOOLTIP_SLOT_PROPS}
        >
            <span style={{ display: "block" }} onMouseOver={(e) => { if (isInPopout(e)) setOpenedMainTooltip(null); }}>
              <SubMenu data-testid={title} label={t(title)} icon={icon} className={getClassName(title, "root")} onClick={() => setMainActive(title)}>
                {lv2Menu?.length > 0 && lv2Menu.map((lv2item, idx) => {
                  return subMenu[idx] ? SidebarSubMenu(lv2item) : SidebarLeafMenu(lv2item)
                })}
              </SubMenu>
            </span>
        </Tooltip>
      )
    }

    if (sideMenu && singleMenu && roleCheck(appRoles, userRoles, reduxKey)) {
      return (
        <MenuItem key={key} className={getClassName(title, "root")} icon={icon} onClick={() => setMainActive(title, key)} >
          {t(title)}
        </MenuItem>
      )
    }

    return "";
  }

  const setPageStyle = (isCollapsed) => {
    const pageContainer = document.getElementById("pageContainer");
    if (pageContainer) {
      if (isCollapsed) {
        pageContainer.classList.remove("collapsed");
      } else {
        pageContainer.classList.add("collapsed");
      }
    }
  }

  const retrieveUserInfo = () => {
    userService.getUserAccessRoles({ username: userData.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserAccess(userAccess);
        },
      )
      .catch(e => { debug(e); });
  }

  useEffect(() => {
    retrieveUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPageStyle(sbCollapsed);
  }, [sbCollapsed]);

  useEffect(() => {
    reduxClickedMenuState?.mainTitle ? setActiveMainTitle(reduxClickedMenuState.mainTitle) : setActiveMainTitle(null);
    reduxClickedMenuState?.subMenu ? setActiveSubMenu(reduxClickedMenuState.subMenu) : setActiveSubMenu(null);
    reduxClickedMenuState?.leafTitle ? setActiveLeafTitle(reduxClickedMenuState.leafTitle) : setActiveLeafTitle(null);

  }, [reduxClickedMenuState?.mainTitle, reduxClickedMenuState?.subMenu, reduxClickedMenuState?.leafTitle])

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <ProSidebar
        collapsed={sbCollapsed}
        transitionDuration={0}
        backgroundColor={themes.sidebar.backgroundColor}
        width={SIDEBAR_WIDTH}
        style={{ zIndex: 1000 }}
      >
        <Box>
          <Box sx={{ display: "flex" }}
            style={sbCollapsed ? {
              padding: "4px 3px 4px 18px",
            } : {
              padding: "4px 3px 4px 17.5px",
            }}
          >
            <img src={sbCollapsed ? compactLogo : logo}
              role="button"
              height={35}
              alt="loading..."
              onClick={handleCollapsedChange}
              style={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
        {userAccess &&
        <Menu
          iconShape="square"
          className="pt-3"
          menuItemStyles={menuItemStyles}
          closeOnClick
        >
          {
            sideMenuList ? sideMenuList.map(element => {
              return SidebarMainMenu(element);
            }) : ""
          }
          <MenuItem
            key="help" className={getClassName("help")}
            icon={<HelpIcon fontSize="large" />}
            target="_blank"
            href="help"
            rel="noopener noreferrer"
          >
            {t("support")}
          </MenuItem>
        </Menu>
        }
      </ProSidebar>
    </Box>
  );
};

export default withTranslation()(Sidebar);
